/**
 * WP-8 combat cinematic — the PURE timeline core (no DOM, no canvas), so it can
 * be unit-tested for determinism. Given the per-site combat results from the
 * rules engine (rules/combat.ts CombatSite[]), it lays out a fixed sequence of
 * timed "beats": each site gets an establishing intro, one beat per shot fired,
 * and a short tail. The canvas viewer (ui/combat-viewer.ts) seeks into this
 * timeline and draws each beat; because every beat's timing and effect seed is
 * derived only from the inputs, a given (sites, seed, time) always paints the
 * same frame — which is what makes the golden screenshot reproducible.
 */
import type { CombatEvent, CombatShipSnapshot, CombatSite } from '../rules/combat';

/** Visual weapon style for a shot, chosen from the attacker class. */
export type Weapon = 'shell' | 'torpedo' | 'air';

export interface IntroBeat {
  kind: 'intro';
  siteIndex: number;
  start: number;
  end: number;
}
export interface ShotBeat {
  kind: 'shot';
  siteIndex: number;
  eventIndex: number;
  event: CombatEvent;
  weapon: Weapon;
  /** deterministic seed for this shot's effect jitter (smoke, splash, shake) */
  seed: number;
  start: number;
  end: number;
}
export interface TailBeat {
  kind: 'tail';
  siteIndex: number;
  start: number;
  end: number;
}
export type Beat = IntroBeat | ShotBeat | TailBeat;

export interface SiteSpan {
  siteIndex: number;
  start: number;
  end: number;
}

export interface Timeline {
  beats: Beat[];
  sites: SiteSpan[];
  /** total run length at 1× speed, ms */
  duration: number;
}

export interface TimelineOpts {
  seed: number;
  /** override default durations (ms) — tests/tuning */
  introMs?: number;
  shotMs?: number;
  tailMs?: number;
}

export const DEFAULT_TIMING = { introMs: 650, shotMs: 850, tailMs: 450 } as const;

/** integer hash → stable per-shot effect seed */
function hash(...nums: number[]): number {
  let h = 0x811c9dc5;
  for (const n of nums) {
    h ^= n | 0;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function weaponFor(ev: CombatEvent): Weapon {
  if (ev.attackerType === 'SS') return 'torpedo';
  if (ev.attackerType === 'CV' || ev.airBonus > 0) return 'air';
  return 'shell';
}

/** Lay out the cinematic as a flat, ordered list of timed beats. */
export function buildTimeline(sites: CombatSite[], opts: TimelineOpts): Timeline {
  const introMs = opts.introMs ?? DEFAULT_TIMING.introMs;
  const shotMs = opts.shotMs ?? DEFAULT_TIMING.shotMs;
  const tailMs = opts.tailMs ?? DEFAULT_TIMING.tailMs;
  const beats: Beat[] = [];
  const spans: SiteSpan[] = [];
  let t = 0;
  sites.forEach((site, si) => {
    const siteStart = t;
    beats.push({ kind: 'intro', siteIndex: si, start: t, end: t + introMs });
    t += introMs;
    site.events.forEach((ev, ei) => {
      beats.push({
        kind: 'shot',
        siteIndex: si,
        eventIndex: ei,
        event: ev,
        weapon: weaponFor(ev),
        seed: hash(opts.seed, si, ei, ev.attackerId, ev.targetId, ev.dmg),
        start: t,
        end: t + shotMs,
      });
      t += shotMs;
    });
    beats.push({ kind: 'tail', siteIndex: si, start: t, end: t + tailMs });
    t += tailMs;
    spans.push({ siteIndex: si, start: siteStart, end: t });
  });
  return { beats, sites: spans, duration: t };
}

/** The beat active at time `ms` (clamped to the last beat). */
export function beatAt(tl: Timeline, ms: number): Beat | null {
  if (tl.beats.length === 0) return null;
  for (const b of tl.beats) if (ms < b.end) return b;
  return tl.beats[tl.beats.length - 1] ?? null;
}

/** Which site is on screen at time `ms`. */
export function siteIndexAt(tl: Timeline, ms: number): number {
  const b = beatAt(tl, ms);
  return b ? b.siteIndex : 0;
}

/** Eased 0→1 progress through a beat at time `ms`. */
export function beatProgress(b: Beat, ms: number): number {
  const span = b.end - b.start;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (ms - b.start) / span));
}

/**
 * HP of each ship in a site after applying the first `uptoEvents` events
 * (the shots already fired). Ships not yet hit keep their snapshot hp; ships at
 * ≤0 are sunk. Pure — used for HP bars and sink animations.
 */
export function hpAfter(
  site: CombatSite, uptoEvents: number,
): Map<number, { snap: CombatShipSnapshot; hp: number; sunk: boolean }> {
  const m = new Map<number, { snap: CombatShipSnapshot; hp: number; sunk: boolean }>();
  for (const s of site.before) m.set(s.id, { snap: s, hp: s.hp, sunk: false });
  for (let i = 0; i < uptoEvents && i < site.events.length; i++) {
    const ev = site.events[i]!;
    const e = m.get(ev.targetId);
    if (e) {
      e.hp -= ev.dmg;
      if (e.hp <= 0) {
        e.hp = 0;
        e.sunk = true;
      }
    }
  }
  return m;
}
